/**
 * Country Configuration
 * Defines country-specific settings for multi-country support
 */

import type { CountryConfig, CurrencyConfig, AddressFormat } from '@schoolidb/types';

/**
 * Supported country codes (including GLOBAL for undetected locations)
 */
export type SupportedCountryCode = 'GLOBAL' | 'IN' | 'US' | 'GB' | 'AE' | 'SG' | 'AU' | 'CA';

/**
 * Global/Default configuration (used when location is not detected or not in supported countries)
 */
const globalConfig: CountryConfig = {
  code: 'GLOBAL',
  name: 'Global',
  currency: {
    code: 'USD',
    symbol: '$',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'YYYY-MM-DD',
  phoneFormat: '+XX XXX XXX XXXX',
  addressFormat: {
    fields: ['street', 'city', 'state', 'postalCode', 'country'],
    postalCodeLabel: 'Postal Code',
  },
  schoolTypes: [
    'Preschool',
    'Primary',
    'Middle',
    'Secondary',
    'High School',
    'College',
    'University',
  ],
  curriculumTypes: [
    'National',
    'International',
    'IB',
    'Cambridge',
    'Montessori',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en'],
  timezone: 'UTC',
};

/**
 * India configuration
 */
const indiaConfig: CountryConfig = {
  code: 'IN',
  name: 'India',
  currency: {
    code: 'INR',
    symbol: '₹',
    position: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'DD/MM/YYYY',
  phoneFormat: '+91 XXXXX XXXXX',
  addressFormat: {
    fields: ['street', 'area', 'city', 'state', 'pincode'],
    postalCodeLabel: 'PIN Code',
  },
  schoolTypes: [
    'Preschool',
    'Primary',
    'Middle',
    'Secondary',
    'Senior Secondary',
    'College',
    'University',
  ],
  curriculumTypes: [
    'CBSE',
    'ICSE',
    'State Board',
    'IB',
    'Cambridge (IGCSE)',
    'Montessori',
    'NIOS',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'hi'],
  timezone: 'Asia/Kolkata',
};

/**
 * United States configuration
 */
const usConfig: CountryConfig = {
  code: 'US',
  name: 'United States',
  currency: {
    code: 'USD',
    symbol: '$',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'MM/DD/YYYY',
  phoneFormat: '+1 (XXX) XXX-XXXX',
  addressFormat: {
    fields: ['street', 'city', 'state', 'zipcode'],
    postalCodeLabel: 'ZIP Code',
  },
  schoolTypes: [
    'Preschool',
    'Elementary',
    'Middle School',
    'High School',
    'College',
    'University',
  ],
  curriculumTypes: [
    'Public',
    'Private',
    'Charter',
    'Magnet',
    'IB',
    'Montessori',
    'Waldorf',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'es'],
  timezone: 'America/New_York',
};

/**
 * United Kingdom configuration
 */
const gbConfig: CountryConfig = {
  code: 'GB',
  name: 'United Kingdom',
  currency: {
    code: 'GBP',
    symbol: '£',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'DD/MM/YYYY',
  phoneFormat: '+44 XXXX XXXXXX',
  addressFormat: {
    fields: ['street', 'city', 'county', 'postcode'],
    postalCodeLabel: 'Postcode',
  },
  schoolTypes: [
    'Nursery',
    'Primary',
    'Secondary',
    'Sixth Form',
    'College',
    'University',
  ],
  curriculumTypes: [
    'National Curriculum',
    'Independent',
    'IB',
    'Cambridge',
    'Scottish Curriculum',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en'],
  timezone: 'Europe/London',
};

/**
 * United Arab Emirates configuration
 */
const aeConfig: CountryConfig = {
  code: 'AE',
  name: 'United Arab Emirates',
  currency: {
    code: 'AED',
    symbol: 'د.إ',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'DD/MM/YYYY',
  phoneFormat: '+971 XX XXX XXXX',
  addressFormat: {
    fields: ['street', 'area', 'city', 'emirate'],
    postalCodeLabel: 'P.O. Box',
  },
  schoolTypes: [
    'Nursery',
    'Primary',
    'Middle',
    'Secondary',
    'College',
    'University',
  ],
  curriculumTypes: [
    'MOE',
    'British',
    'American',
    'IB',
    'CBSE',
    'French',
    'German',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'ar'],
  timezone: 'Asia/Dubai',
};

/**
 * Singapore configuration
 */
const sgConfig: CountryConfig = {
  code: 'SG',
  name: 'Singapore',
  currency: {
    code: 'SGD',
    symbol: 'S$',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'DD/MM/YYYY',
  phoneFormat: '+65 XXXX XXXX',
  addressFormat: {
    fields: ['street', 'building', 'postalCode'],
    postalCodeLabel: 'Postal Code',
  },
  schoolTypes: [
    'Preschool',
    'Primary',
    'Secondary',
    'Junior College',
    'Polytechnic',
    'University',
  ],
  curriculumTypes: [
    'MOE',
    'IB',
    'Cambridge',
    'American',
    'International',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'zh', 'ms', 'ta'],
  timezone: 'Asia/Singapore',
};

/**
 * Australia configuration
 */
const auConfig: CountryConfig = {
  code: 'AU',
  name: 'Australia',
  currency: {
    code: 'AUD',
    symbol: 'A$',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'DD/MM/YYYY',
  phoneFormat: '+61 X XXXX XXXX',
  addressFormat: {
    fields: ['street', 'suburb', 'state', 'postcode'],
    postalCodeLabel: 'Postcode',
  },
  schoolTypes: [
    'Preschool',
    'Primary',
    'Secondary',
    'Senior Secondary',
    'TAFE',
    'University',
  ],
  curriculumTypes: [
    'Australian Curriculum',
    'IB',
    'Cambridge',
    'Montessori',
    'Steiner',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en'],
  timezone: 'Australia/Sydney',
};

/**
 * Canada configuration
 */
const caConfig: CountryConfig = {
  code: 'CA',
  name: 'Canada',
  currency: {
    code: 'CAD',
    symbol: 'C$',
    position: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.',
  },
  dateFormat: 'YYYY-MM-DD',
  phoneFormat: '+1 (XXX) XXX-XXXX',
  addressFormat: {
    fields: ['street', 'city', 'province', 'postalCode'],
    postalCodeLabel: 'Postal Code',
  },
  schoolTypes: [
    'Preschool',
    'Elementary',
    'Middle School',
    'High School',
    'College',
    'University',
  ],
  curriculumTypes: [
    'Provincial',
    'French Immersion',
    'IB',
    'Montessori',
    'Private',
  ],
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'fr'],
  timezone: 'America/Toronto',
};

/**
 * All country configurations
 */
export const countryConfigs: Record<SupportedCountryCode, CountryConfig> = {
  GLOBAL: globalConfig,
  IN: indiaConfig,
  US: usConfig,
  GB: gbConfig,
  AE: aeConfig,
  SG: sgConfig,
  AU: auConfig,
  CA: caConfig,
};

/**
 * Supported countries array for UI components
 */
export const SUPPORTED_COUNTRIES = Object.values(countryConfigs).map((config) => ({
  code: config.code,
  name: config.name,
}));

/**
 * Default country code
 */
export const DEFAULT_COUNTRY_CODE: SupportedCountryCode = 'IN';

/**
 * Get country configuration by code
 * Falls back to India if country code is not supported
 */
export const getCountryConfig = (countryCode: string): CountryConfig => {
  const normalizedCode = countryCode.toUpperCase() as SupportedCountryCode;
  return countryConfigs[normalizedCode] ?? countryConfigs[DEFAULT_COUNTRY_CODE];
};

/**
 * Check if a country code is supported
 */
export const isCountrySupported = (countryCode: string): boolean => {
  const normalizedCode = countryCode.toUpperCase() as SupportedCountryCode;
  return normalizedCode in countryConfigs;
};

/**
 * Get all supported country codes
 */
export const getSupportedCountryCodes = (): SupportedCountryCode[] => Object.keys(countryConfigs) as SupportedCountryCode[];

/**
 * Get country options for select dropdowns
 */
export const getCountryOptions = (): Array<{ value: string; label: string }> => Object.values(countryConfigs).map((config) => ({
    value: config.code,
    label: config.name,
  }));

/**
 * Get school types for a country
 */
export const getSchoolTypes = (countryCode: string): string[] => getCountryConfig(countryCode).schoolTypes;

/**
 * Get curriculum types for a country
 */
export const getCurriculumTypes = (countryCode: string): string[] => getCountryConfig(countryCode).curriculumTypes;

/**
 * Get currency configuration for a country
 */
export const getCurrencyConfig = (countryCode: string): CurrencyConfig => getCountryConfig(countryCode).currency;

/**
 * Get address format for a country
 */
export const getAddressFormat = (countryCode: string): AddressFormat => getCountryConfig(countryCode).addressFormat;

/**
 * Get date format for a country
 */
export const getDateFormat = (countryCode: string): string => getCountryConfig(countryCode).dateFormat;

/**
 * Get phone format for a country
 */
export const getPhoneFormat = (countryCode: string): string => getCountryConfig(countryCode).phoneFormat;

/**
 * Get timezone for a country
 */
export const getTimezone = (countryCode: string): string => getCountryConfig(countryCode).timezone;

export {
  globalConfig,
  indiaConfig,
  usConfig,
  gbConfig,
  aeConfig,
  sgConfig,
  auConfig,
  caConfig,
};
